import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Upload, Download, FileSpreadsheet, FileJson, Users, ShieldCheck, CheckCircle, AlertCircle, FileDown } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ImportExportPage = () => {
  const { user } = useOutletContext();
  
  // Titulares Import
  const [titularFile, setTitularFile] = useState(null);
  const [importingTitulares, setImportingTitulares] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Contacts Import (legacy)
  const [contactFile, setContactFile] = useState(null);
  const [importingContacts, setImportingContacts] = useState(false);
  
  // Exports
  const [exportFormat, setExportFormat] = useState('xlsx');
  const [exportingTitulares, setExportingTitulares] = useState(false);
  const [exportingConsents, setExportingConsents] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  const handleTitularFileChange = (e) => {
    setTitularFile(e.target.files[0]);
    setImportResult(null);
  };

  const handleContactFileChange = (e) => {
    setContactFile(e.target.files[0]);
  };

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/export/titulares/template`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_titulares.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Template descarregado com sucesso');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Erro ao descarregar template');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleImportTitulares = async () => {
    if (!titularFile) {
      toast.error('Selecione um ficheiro');
      return;
    }

    setImportingTitulares(true);
    const formData = new FormData();
    formData.append('file', titularFile);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/import/titulares`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportResult(response.data);
      toast.success(response.data.message);
      setTitularFile(null);
      document.getElementById('titular-file-input').value = '';
    } catch (error) {
      console.error('Error importing:', error);
      toast.error(error.response?.data?.detail || 'Erro ao importar ficheiro');
    } finally {
      setImportingTitulares(false);
    }
  };

  const handleImportContacts = async () => {
    if (!contactFile) {
      toast.error('Selecione um ficheiro');
      return;
    }

    setImportingContacts(true);
    const formData = new FormData();
    formData.append('file', contactFile);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/import/contacts`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`${response.data.contacts?.length || 0} contactos importados com sucesso`);
      setContactFile(null);
      document.getElementById('contact-file-input').value = '';
    } catch (error) {
      console.error('Error importing:', error);
      toast.error('Erro ao importar ficheiro');
    } finally {
      setImportingContacts(false);
    }
  };

  const handleExportTitulares = async () => {
    setExportingTitulares(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/export/titulares?format=${exportFormat}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `titulares.${exportFormat}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Titulares exportados com sucesso');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Erro ao exportar titulares');
    } finally {
      setExportingTitulares(false);
    }
  };

  const handleExportConsents = async () => {
    setExportingConsents(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/export/consents?format=${exportFormat}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `consentimentos.${exportFormat}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Consentimentos exportados com sucesso');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Erro ao exportar consentimentos');
    } finally {
      setExportingConsents(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="import-export-page">
      <div>
        <h1 className="text-4xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Importar / Exportar Dados
        </h1>
        <p className="text-slate-600">
          Gerir ficheiros de titulares, contactos e consentimentos
        </p>
      </div>

      <Tabs defaultValue="titulares" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="titulares" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Titulares
          </TabsTrigger>
          <TabsTrigger value="consents" className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" />
            Consentimentos
          </TabsTrigger>
        </TabsList>

        {/* Titulares Tab */}
        <TabsContent value="titulares">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Import Titulares */}
            <Card className="border-slate-200">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Upload className="w-6 h-6 text-[#10B981]" />
                  <div>
                    <CardTitle>Importar Titulares</CardTitle>
                    <CardDescription className="mt-1">
                      Carregar ficheiros com todos os campos da ficha
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Button
                    variant="outline"
                    onClick={handleDownloadTemplate}
                    disabled={downloadingTemplate}
                    className="w-full mb-4"
                    data-testid="download-template-btn"
                  >
                    <FileDown className="w-4 h-4 mr-2" />
                    {downloadingTemplate ? 'A descarregar...' : 'Descarregar Template Excel'}
                  </Button>
                  
                  <Label htmlFor="titular-file-input">Selecionar Ficheiro</Label>
                  <Input
                    id="titular-file-input"
                    data-testid="titular-file-input"
                    type="file"
                    accept=".csv,.xlsx,.xls,.json"
                    onChange={handleTitularFileChange}
                    className="mt-2"
                  />
                  {titularFile && (
                    <p className="text-sm text-slate-600 mt-2">
                      Ficheiro selecionado: <span className="font-medium">{titularFile.name}</span>
                    </p>
                  )}
                </div>

                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <h4 className="font-semibold text-[#0F172A] mb-2">Campos Suportados:</h4>
                  <div className="text-sm text-slate-600 space-y-1 grid grid-cols-2 gap-x-4">
                    <div>• Email *</div>
                    <div>• Nome Completo</div>
                    <div>• Primeiro Nome</div>
                    <div>• Último Nome / Apelido</div>
                    <div>• Telefone / Telemóvel</div>
                    <div>• Empresa / NIF</div>
                    <div>• Morada / Localidade</div>
                    <div>• Código Postal</div>
                    <div>• Base Legal</div>
                    <div>• ID Autoline</div>
                  </div>
                  <p className="text-xs text-amber-600 mt-2">* Campo obrigatório</p>
                </div>

                <Button
                  data-testid="import-titulares-btn"
                  onClick={handleImportTitulares}
                  disabled={!titularFile || importingTitulares}
                  className="w-full bg-[#10B981] hover:bg-[#10B981]/90"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {importingTitulares ? 'A importar...' : 'Importar Titulares'}
                </Button>

                {importResult && (
                  <Alert className={importResult.errors?.length > 0 ? 'border-amber-300 bg-amber-50' : 'border-emerald-300 bg-emerald-50'}>
                    <AlertDescription>
                      <div className="space-y-2">
                        <div className="flex items-center gap-4">
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Criados: {importResult.created}
                          </Badge>
                          <Badge className="bg-blue-100 text-blue-700">
                            Atualizados: {importResult.updated}
                          </Badge>
                          {importResult.skipped > 0 && (
                            <Badge className="bg-slate-100 text-slate-700">
                              Ignorados: {importResult.skipped}
                            </Badge>
                          )}
                        </div>
                        {importResult.errors?.length > 0 && (
                          <div className="mt-2 text-sm text-amber-700">
                            <p className="font-medium">Avisos:</p>
                            <ul className="list-disc ml-4">
                              {importResult.errors.map((err, idx) => (
                                <li key={idx}>{err}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Export Titulares */}
            <Card className="border-slate-200">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Download className="w-6 h-6 text-[#3B82F6]" />
                  <div>
                    <CardTitle>Exportar Titulares</CardTitle>
                    <CardDescription className="mt-1">
                      Descarregar todos os titulares com os campos completos
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label htmlFor="titulares-format-select">Formato de Exportação</Label>
                  <Select value={exportFormat} onValueChange={setExportFormat}>
                    <SelectTrigger data-testid="titulares-format-select" className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="xlsx">Excel (.xlsx)</SelectItem>
                      <SelectItem value="csv">CSV (.csv)</SelectItem>
                      <SelectItem value="json">JSON (.json)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <h4 className="font-semibold text-[#0F172A] mb-2">Dados Incluídos:</h4>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• Todos os campos da ficha do titular</li>
                    <li>• Dados de contacto (email, telefone, telemóvel)</li>
                    <li>• Informações da empresa (NIF, nome)</li>
                    <li>• Morada completa</li>
                    <li>• Base legal de tratamento</li>
                    <li>• Estado (ativo/inativo)</li>
                  </ul>
                </div>

                <Button
                  data-testid="export-titulares-btn"
                  onClick={handleExportTitulares}
                  disabled={exportingTitulares}
                  className="w-full bg-[#3B82F6] hover:bg-[#3B82F6]/90"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {exportingTitulares ? 'A exportar...' : 'Exportar Titulares'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Consents Tab */}
        <TabsContent value="consents">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Import Contacts (for consents) */}
            <Card className="border-slate-200">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Upload className="w-6 h-6 text-[#10B981]" />
                  <div>
                    <CardTitle>Importar Contactos</CardTitle>
                    <CardDescription className="mt-1">
                      Carregar ficheiros CSV, Excel ou JSON
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label htmlFor="contact-file-input">Selecionar Ficheiro</Label>
                  <Input
                    id="contact-file-input"
                    data-testid="contact-file-input"
                    type="file"
                    accept=".csv,.xlsx,.xls,.json"
                    onChange={handleContactFileChange}
                    className="mt-2"
                  />
                  {contactFile && (
                    <p className="text-sm text-slate-600 mt-2">
                      Ficheiro selecionado: <span className="font-medium">{contactFile.name}</span>
                    </p>
                  )}
                </div>

                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <h4 className="font-semibold text-[#0F172A] mb-2">Formatos Suportados:</h4>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li className="flex items-center gap-2">
                      <FileSpreadsheet className="w-4 h-4" />
                      CSV (.csv)
                    </li>
                    <li className="flex items-center gap-2">
                      <FileSpreadsheet className="w-4 h-4" />
                      Excel (.xlsx, .xls)
                    </li>
                    <li className="flex items-center gap-2">
                      <FileJson className="w-4 h-4" />
                      JSON (.json)
                    </li>
                  </ul>
                </div>

                <Button
                  data-testid="import-contacts-btn"
                  onClick={handleImportContacts}
                  disabled={!contactFile || importingContacts}
                  className="w-full bg-[#10B981] hover:bg-[#10B981]/90"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {importingContacts ? 'A importar...' : 'Importar Contactos'}
                </Button>
              </CardContent>
            </Card>

            {/* Export Consents */}
            <Card className="border-slate-200">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Download className="w-6 h-6 text-[#3B82F6]" />
                  <div>
                    <CardTitle>Exportar Consentimentos</CardTitle>
                    <CardDescription className="mt-1">
                      Descarregar dados em vários formatos
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label htmlFor="consents-format-select">Formato de Exportação</Label>
                  <Select value={exportFormat} onValueChange={setExportFormat}>
                    <SelectTrigger data-testid="consents-format-select" className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="xlsx">Excel (.xlsx)</SelectItem>
                      <SelectItem value="csv">CSV (.csv)</SelectItem>
                      <SelectItem value="json">JSON (.json)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <h4 className="font-semibold text-[#0F172A] mb-2">Dados Incluídos:</h4>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• ID do consentimento</li>
                    <li>• Dados de contacto</li>
                    <li>• Finalidades de tratamento</li>
                    <li>• Estado do consentimento</li>
                    <li>• Data de validação</li>
                    <li>• Método de validação</li>
                    <li>• Data de expiração</li>
                  </ul>
                </div>

                <Button
                  data-testid="export-consents-btn"
                  onClick={handleExportConsents}
                  disabled={exportingConsents}
                  className="w-full bg-[#3B82F6] hover:bg-[#3B82F6]/90"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {exportingConsents ? 'A exportar...' : 'Exportar Consentimentos'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle>Conformidade RGPD</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-900">
              <span className="font-semibold">Nota:</span> Todas as operações de importação e exportação são registadas nos logs de auditoria conforme Artigo 30º do RGPD. 
              Os dados exportados devem ser tratados com medidas de segurança apropriadas conforme Artigo 32º.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ImportExportPage;
